// @owners { team: patients }
import { type DeliveryMethod } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method';
import { type DeliveryMethodType } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method_type';
import { type CartItem } from '@alto/orders_api/types/v1/cart_item';
import {
  ShipmentsEndpoint,
  type ShipmentsEndpointCalculateShipmentsResponse,
} from '@alto/scriptdash/alto/scheduling/patients/v1/shipments_endpoint';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { getEditShipmentID } from '~shared/features/checkout/selectors/getCheckout';
import { getDefaultAddress } from '~shared/features/checkout/selectors/getDefaultAddress';
import { getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import getHasFamily from '~shared/features/users/selectors/getHasFamily';
import { apiEndpointHandler } from '~shared/helpers/api';
import { useSelectorShared } from '~shared/store';

const shipmentsEndpoint = ShipmentsEndpoint(apiEndpointHandler);

type GetCartItemsByDeliveryMethodProps = {
  cartItems: CartItem[];
  addressID?: number;
};

export type CartItemsByDeliveryMethod = Partial<Record<DeliveryMethodType, CartItem[]>>;

const EMPTY_DELIVERY_METHODS: DeliveryMethod[] = [];
const EMPTY_CART_ITEMS_BY_DELIVERY_METHOD: CartItemsByDeliveryMethod = {};

const groupCartItemsByDeliveryMethod = ({
  data,
  cartItemByRxID,
  hasFamily,
}: {
  data: ShipmentsEndpointCalculateShipmentsResponse | undefined;
  cartItemByRxID: Record<number, CartItem>;
  hasFamily: boolean;
}) => {
  const cartItemsByDeliveryMethod: CartItemsByDeliveryMethod = {};
  const deliveryMethods: DeliveryMethod[] = [];
  // edge-case for family accounts, each patient is a separate scheduling context which
  // creates a shipment, and all the shipments are merged to one shipment at double check.
  // Do not show split shipment checkout flow for the family account owner unless the delivery method differs.
  const seenDeliveryMethodNames = hasFamily ? new Set<string>() : null;
  const shipments = data?.data || [];

  shipments?.forEach((shipment) => {
    const { prescription_ids, delivery_method } = shipment;
    const deliveryMethodName = delivery_method.name;

    if (!cartItemsByDeliveryMethod[deliveryMethodName]) {
      cartItemsByDeliveryMethod[deliveryMethodName] = [];
    }

    prescription_ids.forEach((id) => {
      const cartItem = cartItemByRxID[id];

      if (cartItem) {
        cartItemsByDeliveryMethod[deliveryMethodName]?.push(cartItem);
      }
    });

    if (!seenDeliveryMethodNames?.has(deliveryMethodName)) {
      seenDeliveryMethodNames?.add(deliveryMethodName);
      deliveryMethods.push(delivery_method);
    }
  });

  return { cartItemsByDeliveryMethod, deliveryMethods };
};

const useCartItemsByDeliveryMethod = ({
  cartItems,
  addressID,
  hasFamily,
}: GetCartItemsByDeliveryMethodProps & { hasFamily: boolean }) => {
  const editShipmentId = useSelectorShared(getEditShipmentID);

  const { cartItemByRxID, prescriptionIDs } = useMemo(() => {
    const cartItemByRxID: Record<number, CartItem> = {};
    const prescriptionIDs = cartItems.reduce<number[]>((ids, item) => {
      if (item.resource_type === 'Prescription') {
        cartItemByRxID[item.resource_id] = item;
        ids.push(item.resource_id);
      }
      return ids;
    }, []);
    return { cartItemByRxID, prescriptionIDs };
  }, [cartItems]);

  const select = useCallback(
    (data: ShipmentsEndpointCalculateShipmentsResponse) => {
      return groupCartItemsByDeliveryMethod({ data, cartItemByRxID, hasFamily });
    },
    [cartItemByRxID, hasFamily],
  );

  const { data } = useQuery({
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @alto/prefer-query-key-factory
    queryKey: ['scheduling-shipments-calculateShipments', { prescriptionIDs, addressID }],
    queryFn: () =>
      shipmentsEndpoint.calculateShipments(
        { prescription_ids: prescriptionIDs, address_id: addressID ?? 0, shipment_id: editShipmentId },
        { splitArrayParams: true },
      ),
    select,
    enabled: prescriptionIDs.length !== 0 && !!addressID,
  });

  return { data };
};

/**
 * fetches cart items by delivery method
 */
export const useCartDeliveryMethods = () => {
  const { cartItems } = useCartData();
  const hasFamily = useSelectorShared(getHasFamily);
  const orderAddressId = useSelectorShared(getOrderAddressId);
  const defaultAddress = useSelectorShared(getDefaultAddress);
  // Use default address when order has no address (e.g. cart before checkout) so we can determine split shipment and hide pricing summary
  const addressID = orderAddressId ?? defaultAddress?.id;
  const { data } = useCartItemsByDeliveryMethod({
    cartItems,
    addressID,
    hasFamily,
  });

  return {
    cartItemsByDeliveryMethod: data?.cartItemsByDeliveryMethod ?? EMPTY_CART_ITEMS_BY_DELIVERY_METHOD,
    deliveryMethods: data?.deliveryMethods ?? EMPTY_DELIVERY_METHODS,
  };
};
